/**
 * 모바일 쪽지 상세 시트 (view/mobile/note/list.html) — 데스크톱 inbox/outbox 슬라이드 패널의 읽기 전용 축약 + 답장.
 *
 * 열기: window 이벤트 'open-note' 로 { wrSeq } 전달 (행 요소 data-note-id)
 *   - GET /note/api/{wrSeq} (수신자면 서버가 읽음 처리) → note 표시.
 *   - 읽음으로 바뀌면 행 DOM 의 is-unread 를 걷어내고 window 'note-read' 를 쏴 앱바 noteBadge 가 refresh 한다(페이지당 noteBadge 1개 규칙 유지).
 * 답장(계획서 §1-2 — 읽기 전용의 유일한 예외): POST /note/api/send { content, receiverIds:[senderId], sendToSelf:false, conType, conIdx }
 *   - 데스크톱 reply() 미러: 연계는 conType 1/2/4/7 만 승계, 나머지는 비연결.
 *   - 자기 자신에게 보낸 쪽지는 senderId == currentUserId → 서버가 본인 id 도 수신자로 허용(주소록 조회 없음).
 * 연계 링크: relUrl(/suit/view|/client/view) 만 /m 접두 — 자문·업무·문서 링크는 모바일 범위 밖(표시만).
 * 세션 만료: 인터셉터가 fetch 에는 302(→ res.redirected) 또는 401 JSON 을 주므로 /m/login 으로 보낸다.
 */
export default (opts = {}) => ({
    box: opts.box || 'inbox',
    currentUserId: opts.currentUserId || '',
    isOpen: false,
    loading: false,
    note: null,
    isReceiver: false,
    replyOpen: false,
    replyText: '',
    sending: false,
    CONTENT_MAX: 2000,

    get mobileRelUrl() {
        const u = this.note && this.note.relUrl;
        if (!u) return null;
        return (u.startsWith('/suit/view/') || u.startsWith('/client/view/')) ? '/m' + u : null;
    },
    get canReply() {
        return !!this.note && !this.note.canceled && this.box === 'inbox';
    },
    get replyRemain() {
        return this.CONTENT_MAX - this.replyText.length;
    },

    async open(wrSeq) {
        if (!wrSeq) return;
        this.isOpen = true;
        this.loading = true;
        this.note = null;
        this.replyOpen = false;
        this.replyText = '';
        document.body.style.overflow = 'hidden';
        try {
            const res = await fetch('/note/api/' + wrSeq, { headers: { 'X-Requested-With': 'fetch' } });
            if (res.redirected || res.status === 401) { window.location.href = '/m/login'; return; }
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.message || 'HTTP ' + res.status);
            this.note = json.data.note;
            this.isReceiver = !!json.data.isReceiver;
            if (this.isReceiver && this.note.read) this.markRowRead(this.note.wrSeq);
        } catch (e) {
            this.close();
            this.toast('error', '오류', e.message || '쪽지를 불러오지 못했습니다.');
        } finally {
            this.loading = false;
        }
    },

    close() {
        this.isOpen = false;
        this.replyOpen = false;
        document.body.style.overflow = '';
    },

    /** 행 DOM 갱신(미확인 강조 해제) + 앱바 뱃지 갱신 */
    markRowRead(wrSeq) {
        const row = document.querySelector('.m-row-note[data-note-id="' + wrSeq + '"]');
        if (row && row.classList.contains('is-unread')) {
            row.classList.remove('is-unread');
            const tag = row.querySelector('[data-read-tag]');
            if (tag) { tag.textContent = '확인'; tag.className = 'tag tag-tonal'; }
        }
        window.dispatchEvent(new CustomEvent('note-read'));
    },

    openReply() {
        this.replyOpen = true;
        this.$nextTick(() => { const ta = this.$refs.replyInput; if (ta) ta.focus(); });
    },

    async sendReply() {
        if (this.sending || !this.note) return;
        const content = this.replyText.trim();
        if (!content) { this.toast('warning', '알림', '내용을 입력하세요.'); this.$refs.replyInput?.focus(); return; }
        if (content.length > this.CONTENT_MAX) { this.toast('warning', '알림', '내용은 2000자 이내로 입력하세요.'); return; }
        const linked = ['1', '2', '4', '7'].includes(this.note.conType);
        this.sending = true;
        try {
            const res = await fetch('/note/api/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' },
                body: JSON.stringify({
                    content,
                    receiverIds: [this.note.senderId],
                    sendToSelf: false,
                    conType: linked ? this.note.conType : '0',
                    conIdx: linked ? (this.note.conIdx * 1 || 0) : 0
                })
            });
            if (res.redirected || res.status === 401) { window.location.href = '/m/login'; return; }
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.message || 'HTTP ' + res.status);
            this.toast('success', '전송 완료', (this.note.senderName || '') + ' 님에게 답장을 보냈습니다.');
            this.close();
        } catch (e) {
            this.toast('error', '전송 실패', e.message || '답장을 보내지 못했습니다.');
        } finally {
            this.sending = false;
        }
    },

    toast(type, title, message) {
        window.dispatchEvent(new CustomEvent('show-toast', { detail: { type, title, message } }));
    }
});
